import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import isString from "lodash/isString";
import merge from "lodash/merge";

import Sentry from "@similarweb/sentry-logger";
import type * as ITrack from "@similarweb/sw-track";

import type { IComponent } from "app/@types/ISwSettings";
import categoryService from "app/common/services/categoryService";
import type { ECategoryType } from "app/common/services/categoryService.types";
import { chosenItems } from "app/common/services/chosenItems";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { CookieManager } from "app/components/cookie-manager/CookieManager";
import DashboardService from "app/components/dashboard/services/DashboardService";
import type { IDashboard } from "app/components/dashboard/services/IDashboard";
import { getParsedDataFromLocalStorage } from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { BRAND_PROTECTION_CAMPAIGNS_CONFIGS_KEY } from "app/pages/digital-marketing/brand-monitoring-campaigns/infra/BrandMonitoringCampaignsContext";
import { llmTrackerCampaignsQueryClient } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/llmTrackerCampaignsQueryClient";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import {
    RANK_TRACKER_CAMPAIGNS_CONFIG_KEY,
    RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY,
} from "app/common/campaigns/KeywordsCampaignConsts";
import type { IKeywordsCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { createRankTrackerQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import RegexPatterns from "app/services/RegexPatterns";
import { resolveSegmentsTrackingAttributes } from "app/services/track/utils/resolveSegmentsTrackingAttributes";
import { GrowthbookService } from "app/services/trackingServices/GrowthBookService";
import { userRoleToAccountRole } from "app/UtilitiesAndConstants/UtilityFunctions/userRoleUtils";
import { buildDemandPageViewCustomData } from "pro-features/pages/module start page/src/keyword analysis/topics/helpers/buildDemandPageViewCustomData";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import queryClient from "single-spa/react-app/query-client";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import type { PageId } from "app/@types/userdata";

import type { DateRangeMode } from "./utils/resolveDateRangeMode";
import { resolveDateRangeMode } from "./utils/resolveDateRangeMode";
import { resolveHistoricalData } from "./utils/resolveHistoricalData";

/**
 *  Category divider
 *  Industry Analysis: "Arts_and_Entertainment~Fashion_and_Modeling"
 *  App Category Analysis: "Games > Educational"
 * @type {RegExp}
 */
const CATEGORY_DIVIDER = /~| > |\//;

interface IStateParams {
    [key: string]: string;
}

type ICustomUrlBuilder = (pageId: PageId, params: IStateParams, toState: IRouterState) => ITrack.ICustomDimensionData;
type IAsyncCustomUrlBuilder = (
    pageId: PageId,
    params: IStateParams,
    toState: IRouterState,
) => Promise<ITrack.ICustomDimensionData>;

interface ICustom_attributes {
    date_range_mode: DateRangeMode | null;
    max_history?: number | null;
    granularity?: string;
}

type Query = ITrack.ICustomDimensionQuery & ICustom_attributes;

let locale = "";

const cookieManager: CookieManager = new CookieManager();

function getPageId(pageId: PageId) {
    return pageId.pageId || `${pageId.section}-${pageId.subSection}-${pageId.subSubSection}`;
}

function getLocale() {
    if (!locale) {
        locale = cookieManager.getCookie("locale") || "en-us";
    }
    return locale;
}

const joinId = (arr: ReadonlyArray<string | number>) => arr.join(",");
const joinName = (arr: ReadonlyArray<string>) => arr.join(" | ");

export function getIsSwUser() {
    const user = swSettings.user;
    return user.impersonateSource != null || user.username.toLowerCase().endsWith("@similarweb.com");
}

function getIsAutomationUser() {
    const user = swSettings.user;
    return user.username.startsWith("galil+pro") && user.username.endsWith("@similarweb.com");
}

function getIdentity(): ITrack.ICustomDimensionIdentity {
    const user = swSettings.user;

    return {
        subscription_id: user.subscription,
        base_product: user.plan,
        user_id: user.id,
        account_id: user.accountId,
        email: user.username,
        role: userRoleToAccountRole[user.userRole as keyof typeof userRoleToAccountRole],
        ...(user.impersonateSource && {
            is_impersonate: true,
            impersonator_user_id: user.impersonateSource.id,
            impersonator_email: user.impersonateSource.username,
        }),
    };
}

function getPath(pageId: PageId, params: IStateParams): ITrack.ICustomDimensionPath {
    const defaultSectionAttributes = {
        section: isFunction(pageId.section) ? pageId.section(params) : pageId.section,
        sub_section: isFunction(pageId.subSection) ? pageId.subSection(params) : pageId.subSection,
        sub_sub_section: isFunction(pageId.subSubSection) ? pageId.subSubSection(params) : pageId.subSubSection,
    };
    const sectionAttributes = defaultSectionAttributes;
    return {
        ...sectionAttributes,
        page_id: getPageId(pageId),
        sidebar_version: "3.1",
    };
}

function getDomainType(type: string | undefined): ITrack.DOMAIN_TYPE | undefined {
    switch (type) {
        case "*":
            return "WITH_SUBDOMAINS";
        case "-":
            return "WITHOUT_SUBDOMAINS";
        default:
            return undefined;
    }
}

function getWebSourceType(type: string | undefined): ITrack.WEB_SOURCE | undefined {
    if (!isString(type)) {
        return undefined;
    }

    switch (type.toLowerCase()) {
        case "total":
            return "TOTAL";
        case "desktop":
            return "DESKTOP";
        case "mobile":
        case "mobileweb":
            return "MOBILEWEB";
        default:
            return undefined;
    }
}

const getGBTrackingCustomData = () => GrowthbookService.pageViewTracking();

function commonBuilder(pageId: PageId, params: IStateParams) {
    let query: Query | undefined;
    let filters: ITrack.ICustomDimensionFilters | undefined;

    if (!isEmpty(params)) {
        query = {
            country: parseInt(params.country) || undefined,
            date_range: params.duration,
            order_by: params.orderby,
            date_range_mode: resolveDateRangeMode(),
            max_history: resolveHistoricalData(),
        };

        filters = {
            tab: params.selectedTab || params.tab || undefined,
            page_number: parseInt(params.page) || undefined,
            web_source: getWebSourceType(params.webSource),
        };
    }
    return {
        page_id: getPageId(pageId),
        path: getPath(pageId, params),
        identity: getIdentity(),
        is_sw_user: getIsSwUser(),
        lang: getLocale(),
        query,
        filters,
        custom_data: getGBTrackingCustomData(),
    };
}

function withCommon<TSource1, TSource2, TSource3>(
    pageId: PageId,
    params: IStateParams,
    source1: TSource1,
    source2?: TSource2,
    source3?: TSource3,
) {
    return merge(
        {},
        { custom_data: getGBTrackingCustomData() },
        commonBuilder(pageId, params),
        source1,
        source2,
        source3,
    );
}

const demandAnalysisBuilder: IAsyncCustomUrlBuilder = async (
    pageId: PageId,
    params: IStateParams,
    toState: IRouterState,
): Promise<ITrack.ICustomDimensionData> => {
    try {
        const custom_data = await buildDemandPageViewCustomData(toState.name ?? "", params);
        return withCommon(pageId, params, { custom_data });
    } catch (error) {
        Sentry.error("Error building demand analysis custom data", error as Error);
        return withCommon(pageId, params, {});
    }
};

const industryAnalysisBuilder: ICustomUrlBuilder = (
    pageId: PageId,
    params: IStateParams,
    _toState: IRouterState,
): ITrack.ICustomDimensionData => {
    let category: (Partial<ITrack.ICustomDimensionCategory> & { category_type?: ECategoryType }) | undefined;
    const categoryStr = Array.isArray(params.category) ? params.category[0] : params.category;

    if (categoryStr) {
        const categoryParts: string[] = categoryStr.split(CATEGORY_DIVIDER);
        const isCustomCategory = UserCustomCategoryService.isCustomCategory(categoryStr);
        if (!isCustomCategory) {
            category = {
                main_category: categoryParts[0],
                sub_category: categoryParts[1],
            };
        } else {
            const customCategory = categoryService.categoryQueryParamToCategoryObject(categoryStr);
            if (customCategory && customCategory.id) {
                const { domains = [], segments = [], categoryId, name, isShared, categoryType } = customCategory;

                category = {
                    custom_category_id: categoryId,
                    custom_category_name: name,
                    is_shared_custom_category: isShared,
                    category_type: categoryType,
                    domains_amount: domains.length,
                    segments_amount: segments.length,
                    entities_amount: domains.length + segments.length,
                };
            } else {
                Sentry.error(`Custom Category not found`, new Error(`Custom Category does not exist: ${categoryStr}`));
                category = {
                    custom_category_id: 0,
                    custom_category_name: "NOTFOUND",
                    is_shared_custom_category: false,
                };
            }
        }
    }

    const filters: ITrack.ICustomDimensionFilters = {
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        app_mode: (params.mode && params.mode.toLowerCase().split(" ").join("_")) || null,
    };

    return withCommon(pageId, params, { category, filters });
};

const AD_INTELLIGENCE_INDUSTRY_MODE = "industry";

// Custom-url type for Ad Intelligence Display Advertising states (the ones hosting
// industry mode). Deliberately distinct from the legacy "AdIntelligence" type, which
// routes to websiteBuilder. States opt in via data.getCustomUrlType.
export const AD_INTELLIGENCE_DISPLAY_TYPE = "AdIntelligenceDisplay";

export const buildAdIntelligenceIndustryCategory = (industry: string): Partial<ITrack.ICustomDimensionCategory> => {
    if (!UserCustomCategoryService.isCustomCategory(industry)) {
        const categoryParts: string[] = industry.split(CATEGORY_DIVIDER);
        return {
            main_category: categoryParts[0],
            sub_category: categoryParts[1],
        };
    }
    const customCategory = categoryService.categoryQueryParamToCategoryObject(industry);
    if (customCategory && customCategory.categoryId) {
        return {
            custom_category_id: customCategory.categoryId,
            custom_category_name: customCategory.name ?? customCategory.text,
            is_shared_custom_category: Boolean(customCategory.isShared),
        };
    }
    Sentry.error(`Custom Category not found`, new Error(`Custom Category does not exist: ${industry}`));
    return {
        custom_category_id: 0,
        custom_category_name: "NOTFOUND",
        is_shared_custom_category: false,
    };
};

// Ad Intelligence pages support two mutually exclusive modes driven by URL params:
// domain mode (`key`) and industry mode (`industry`). Without `industry` this builder
// is identical to the default commonBuilder, so domain mode is unaffected.
const adIntelligenceBuilder: ICustomUrlBuilder = (
    pageId: PageId,
    params: IStateParams,
    _toState: IRouterState,
): ITrack.ICustomDimensionData => {
    const industry = Array.isArray(params.industry) ? params.industry[0] : params.industry;
    if (!industry) {
        return commonBuilder(pageId, params);
    }
    return withCommon(pageId, params, {
        category: buildAdIntelligenceIndustryCategory(industry),
        custom_data: { mode: AD_INTELLIGENCE_INDUSTRY_MODE },
    });
};

const getQuery = (params: IStateParams): Query => {
    const { country, duration } = params;
    const query = {
        country: parseInt(country) || undefined,
        date_range: duration,
        date_range_mode: resolveDateRangeMode(),
        max_history: resolveHistoricalData(),
    };
    return query;
};

const getFilters = (params: IStateParams): ITrack.ICustomDimensionFilters => {
    const filters = {
        domain_type: getDomainType(params.isWWW),
        web_source: getWebSourceType(params.state || params.webSource),
    };
    return filters;
};

const segmentAnalysisBuilder: ICustomUrlBuilder = (pageId, params, _toState) => {
    const query = getQuery(params);
    const filters = getFilters(params);
    const custom_data = resolveSegmentsTrackingAttributes();
    return withCommon(pageId, params, {
        query,
        filters,
        custom_data,
    });
};

const ENTITY_TYPE_TO_BUILDER: Partial<Record<NonNullable<IRouterState["entityType"]>, ICustomUrlBuilder>> = {
    industry: industryAnalysisBuilder,
    segment: segmentAnalysisBuilder,
};

const benchmarkBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams) => {
    let query: Query | undefined;
    let filters: ITrack.ICustomDimensionFilters | undefined;

    if (!isEmpty(params)) {
        query = {
            country: parseInt(params.country) || undefined,
            date_range: params.duration,
            order_by: params.orderby,
            date_range_mode: resolveDateRangeMode(),
            max_history: resolveHistoricalData(),
            granularity: params.granularity,
        };

        filters = {
            tab: params.selectedTab || params.tab || undefined,
            page_number: parseInt(params.page) || undefined,
            web_source: getWebSourceType(params.webSource),
        };
    }

    return {
        page_id: getPageId(pageId),
        path: getPath(pageId, params),
        identity: getIdentity(),
        is_sw_user: getIsSwUser(),
        custom_data: { is_asset_owner: params?.isAssetOwner },
        lang: getLocale(),
        query,
        filters,
    };
};

const aiTrackerBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams) => {
    const { campaignId, model } = params;

    // Get campaign from cache directly to avoid async issues in tracking
    let currentCampaign: IAiTrackerCampaign | undefined;
    try {
        const campaigns = llmTrackerCampaignsQueryClient.getCampaignsFromCache();
        if (campaigns && Array.isArray(campaigns)) {
            currentCampaign = campaigns.find((campaign: IAiTrackerCampaign) => campaign.id === campaignId);
        }
    } catch (error) {
        Sentry.error("Error getting campaign from cache for tracking", error as Error);
    }

    if (!currentCampaign) {
        return commonBuilder(pageId, params);
    }

    const custom_data: ITrack.ICustomDimensionCustomData = {
        entity_id: campaignId,
        entity_name: currentCampaign?.name,
        number_of_topics: currentCampaign?.topics?.length ?? 0,
        topics_filter: params.topicsFilter ?? "",
        model,
    };

    return withCommon(pageId, params, { custom_data });
};

const appsBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams): ITrack.ICustomDimensionData => {
    const appObject = chosenItems.$first();
    const all = chosenItems.$all();

    const query: ITrack.ICustomDimensionQuery = {
        device_os: params.device,
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        store: appObject.AppStore ? (appObject.AppStore === "Apple" ? "ios" : "google-play") : null,
    };

    let category: ITrack.ICustomDimensionCategory | null = null;
    const categoryStr = appObject.Category;

    if (categoryStr) {
        const categoryParts: string[] = categoryStr.split(CATEGORY_DIVIDER);

        category = {
            main_category: categoryParts[0],
            sub_category: categoryParts[1],
        };
    }

    const filters: ITrack.ICustomDimensionFilters = {
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        app_mode: (params.mode && params.mode.toLowerCase().split(" ").join("_")) || null,
    };

    const entity: ITrack.ICustomDimensionEntity = {
        entity_id: joinId(all.map((i: { Id: string | number }) => i.Id)),
        //For mobile app name we remove emojies because it breaks database query.
        entity_name: joinName(all.map((i: { Title: string }) => RegexPatterns.removeEmojis(i.Title))),
    };

    return withCommon(pageId, params, { query, category, filters, entity });
};

const topappsBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams): ITrack.ICustomDimensionData => {
    const query: ITrack.ICustomDimensionQuery = {
        device_os: params.device,
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        store: params.store ? (params.store === "Apple" ? "ios" : "google-play") : null,
    };

    let category: ITrack.ICustomDimensionCategory | null = null;
    const categoryStr = params.category;

    if (categoryStr) {
        const categoryParts: string[] = categoryStr.split(CATEGORY_DIVIDER);

        category = {
            main_category: categoryParts[0],
            sub_category: categoryParts[1],
        };
    }

    const filters: ITrack.ICustomDimensionQuery = {
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        app_mode: (params.mode && params.mode.toLowerCase().split(" ").join("_")) || null,
    };

    return withCommon(pageId, params, { query, category, filters });
};

const googlePlayKeywords: ICustomUrlBuilder = (pageId: PageId, params: IStateParams): ITrack.ICustomDimensionData => {
    const category: ITrack.ICustomDimensionCategory = {
        main_category: params.keyword,
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined'.ts(2322)
        sub_category: null,
    };
    return withCommon(pageId, params, { category });
};

const keywordsBuilder: IAsyncCustomUrlBuilder = async (
    pageId: PageId,
    params: IStateParams,
): Promise<ITrack.ICustomDimensionData> => {
    const { keyword, keywordGroupId } = params;
    if (!keyword && !keywordGroupId) {
        return withCommon(pageId, params, {});
    }
    let category: Partial<ITrack.ICustomDimensionCategory>;
    let custom_data: ITrack.ICustomDimensionCustomData = {};

    const groupId = keyword ? keyword.substring(1) : keywordGroupId;

    let keywordGroup: IUserKeywordGroup | null = null;

    if (groupId) {
        try {
            keywordGroup = await keywordGroupsQueryClient.getGroupById(groupId);
        } catch (error) {
            Sentry.error(`Error fetching keyword group`, error as Error);
            keywordGroup = null;
        }
    }
    if (keywordGroup?.Id) {
        const isGroupMine = keywordGroup.UserId === getIdentity().user_id?.toString();
        const isGroupShared =
            (keywordGroup.sharedWithAccounts?.length ?? 0) > 0 || (keywordGroup.sharedWithUsers?.length ?? 0) > 0;

        category = {
            custom_category_id: keywordGroup.Id,
            custom_category_name: keywordGroup.Name,
            is_shared_custom_category: isGroupShared,
        };
        custom_data = {
            is_asset_owner: isGroupMine,
        };
    } else {
        category = {
            main_category: params.keyword,
            // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string' — preserves pre-migration behavior in tracking payload (sub_category is required in ICustomDimensionCategoryInfo but was historically null when no sub-category exists)
            sub_category: null,
        };
    }

    return withCommon(pageId, params, { category, custom_data });
};

const getCurrentCampaign = (
    section: string,
    campaignId: string,
    demoCampaigns: IKeywordsCampaignData[],
): IKeywordsCampaignData | undefined => {
    const cachedCampaign = queryClient.getQueryData<IKeywordsCampaignData>(
        createRankTrackerQueryKey(RankTrackerQueryType.CurrentCampaign, [campaignId]),
    );
    if (cachedCampaign) {
        return cachedCampaign;
    }

    const localStorageKey =
        section === "Brand Protection" ? BRAND_PROTECTION_CAMPAIGNS_CONFIGS_KEY : RANK_TRACKER_CAMPAIGNS_CONFIG_KEY;
    const campaignsConfigurationsFromLocalStorage =
        getParsedDataFromLocalStorage<Array<IKeywordsCampaignData>>(localStorageKey) ?? [];

    const currentCampaign = [...campaignsConfigurationsFromLocalStorage, ...demoCampaigns].find(
        (campaign) => campaign.id === campaignId,
    );
    return currentCampaign;
};

export const getIsAssetsOwnerForCampaign = (
    campaignId: string,
    currentCampaign: IKeywordsCampaignData | undefined,
    demoCampaigns: IKeywordsCampaignData[],
): boolean => {
    const isDemoCampaign = demoCampaigns && demoCampaigns.some((campaign) => campaign.id === campaignId);
    return !currentCampaign?.isShared && !isDemoCampaign;
};

const performanceAddonCampaignsBuilder: ICustomUrlBuilder = (
    pageId: PageId,
    params: IStateParams,
): ITrack.ICustomDimensionData => {
    const { campaignId } = params;
    const { subSubSection, section } = pageId;
    const ignoreStates = ["Homepage", "Campaign Creation"];
    let custom_data: ITrack.ICustomDimensionCustomData = {};
    if (subSubSection && ignoreStates.includes(subSubSection)) {
        return commonBuilder(pageId, params);
    }
    let demoCampaigns: IKeywordsCampaignData[] = [];
    if (section !== "Brand Protection") {
        demoCampaigns = getParsedDataFromLocalStorage(RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY) ?? [];
    }
    const currentCampaign = getCurrentCampaign(section, campaignId, demoCampaigns);
    const isAssetOwner = getIsAssetsOwnerForCampaign(campaignId, currentCampaign, demoCampaigns);

    custom_data = {
        asset_type: (currentCampaign && "Rank Tracker") || undefined,
        asset_name: currentCampaign?.name,
        asset_id: (currentCampaign && campaignId) || undefined,
        is_asset_owner: currentCampaign && isAssetOwner,
        entity_type: (currentCampaign && "Asset") || undefined,
        entity_id: (currentCampaign && campaignId) || undefined,
        entity_name: currentCampaign?.name,
        number_of_keywords_in_campaign: currentCampaign?.keywordCount,
        number_of_searchEngines_in_campaign: currentCampaign?.scrapingConfigurations?.length,
        number_of_competitors_in_campaign: currentCampaign?.competitors?.length,
        has_tags: Boolean(currentCampaign?.tags),
    };

    return withCommon(pageId, params, { custom_data });
};

const deepInsightsBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams): ITrack.ICustomDimensionData => {
    const custom_data: ITrack.ICustomDimensionCustomData = {
        di_types: params.types,
    };

    return withCommon(pageId, params, { custom_data });
};

const websiteBuilder: ICustomUrlBuilder = (pageId, params, toState): ITrack.ICustomDimensionData => {
    const sites: string[] = chosenSites.get();
    const siteInfo = chosenSites.getInfo(sites[0]);
    let searchTypes: string[] = [];

    if (params.pageFilter) {
        const pageFilterParsed: Array<{ searchType: string }> = JSON.parse(params.pageFilter);
        searchTypes = pageFilterParsed.map(({ searchType }) => searchType);
    }

    const entity: ITrack.ICustomDimensionEntity = {
        entity_id: joinId(sites),
        entity_name: joinName(sites),
        entity_type: searchTypes.length ? joinName(searchTypes) : undefined,
    };
    const query = getQuery(params);
    let category: ITrack.ICustomDimensionCategory | null = null;

    if (siteInfo && siteInfo.category) {
        const categoryParts: string[] = siteInfo.category.split(CATEGORY_DIVIDER);

        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | number | undefined' for custom_category_id
        category = {
            main_category: categoryParts[0],
            sub_category: categoryParts[1],
            custom_category_id: null,
        };
    }
    const filters = getFilters(params);

    const custom_data: ITrack.ICustomDimensionCustomData = {
        keywords_filters: params.Keywords_filters,
        mmx_beta: params.mmx_beta,
    };

    forEach(params, function (value, key) {
        if (key.indexOf("_orderby") > -1) {
            query.order_by = value;
        } else if (key.indexOf("_page") > -1) {
            filters.page_number = parseInt(value) || undefined;
        }
    });

    return withCommon(pageId, params, {
        entity,
        category,
        query,
        filters,
        custom_data,
    });
};

const dashboardBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams): ITrack.ICustomDimensionData => {
    let category: ITrack.ICustomDimensionCategory | null = null;

    switch (pageId.subSection) {
        case "exist": // existing dashboard
        case "created": {
            // created dashboard
            const dashboard: IDashboard = DashboardService.getDashboardById(params.dashboardId);
            if (dashboard) {
                category = {
                    custom_category_id: dashboard.id,
                    custom_category_name: dashboard.title,
                    is_shared_custom_category: false,
                };
            } else {
                category = {
                    custom_category_id: params.dashboardId,
                    custom_category_name: "",
                    is_shared_custom_category: false,
                };
            }
            break;
        }
        case "new": // new dashboard page
            category = null;
    }

    return withCommon(pageId, params, {
        category,
    });
};

const connectedAccountsBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams, toState) => {
    const swConnectedAccountsService = connectedAccountsServiceFactory();

    const res = appsBuilder(pageId, params, toState);
    res.custom_data = res.custom_data || {};
    res.custom_data.connected_accounts = swConnectedAccountsService.hasGooglePlayConnectedAccount();

    return res;
};

const forbiddenStateBuilder: ICustomUrlBuilder = (pageId: PageId, params: IStateParams) => {
    return withCommon(pageId, params, { query: { not_permitted: true } });
};

/////////////
// HELPERS
/////////////

/**
 * resolve the builder
 * @param {string} type
 * @returns {ICustomUrlBuilder | IAsyncCustomUrlBuilder}
 */
function getBuilder(type: string, toState: IRouterState): ICustomUrlBuilder | IAsyncCustomUrlBuilder {
    const getNearestStateWithEntityType = proNavigator.getNearestStateWithHOF("entityType");
    const nearestStateWithEntityType = getNearestStateWithEntityType(toState);
    const entityType = nearestStateWithEntityType?.entityType;
    const entityTypeBuilder = entityType ? ENTITY_TYPE_TO_BUILDER[entityType] : undefined;
    if (entityTypeBuilder) {
        return entityTypeBuilder;
    }
    switch (type) {
        case "apps":
        case "appAnalysis":
        case "Competitive Research-apps":
        case "Competitive Research-da":
            return appsBuilder;
        case "topapps":
            return topappsBuilder;
        case "industryAnalysis":
        case "Market Research":
        case "Market Analysis":
            return industryAnalysisBuilder;
        case "keywordAnalysis":
        case "Digital Marketing - Keywords":
            return keywordsBuilder;
        case "demandAnalysis":
        case "searchInterestsAnalysis":
        case "Market Research - Keywords":
            return demandAnalysisBuilder;
        case "keywords":
            return googlePlayKeywords;
        case AD_INTELLIGENCE_DISPLAY_TYPE:
            return adIntelligenceBuilder;
        case "website":
        case "AdIntelligence":
        case "Digital Marketing":
        case "Competitive Research":
        case "Acquisition Channels":
        case "websiteAnalysis":
        case "digitalsuite":
            return websiteBuilder;
        case "dashboard":
            return dashboardBuilder;
        case "connectedAccounts":
            return connectedAccountsBuilder;
        case "DeepInsights":
            return deepInsightsBuilder;
        case "forbiddenState":
            return forbiddenStateBuilder;
        case "Search Tracking":
            return performanceAddonCampaignsBuilder;
        case "benchmark":
            return benchmarkBuilder;
        case "homepage":
            return benchmarkBuilder;
        case "Gen AI Intelligence":
            return aiTrackerBuilder;
        default:
            return commonBuilder;
    }
}

/**
 * build
 * @param {string} type
 * @param {PageId} pageId
 * @param params
 * @param {IComponent} component
 * @returns {Promise<ITrack.ICustomDimensionData>}
 */
export const build = async (
    toState: IRouterState,
    pageId: PageId,
    params: any,
    component: IComponent,
): Promise<ITrack.ICustomDimensionData> => {
    const type = toState.data.getCustomUrlType(toState);
    const transformFunc = getBuilder(type, toState);

    try {
        const result = transformFunc(pageId, params, toState);
        const customUrlObj: ITrack.ICustomDimensionData = await Promise.resolve(result);
        const notPermitted = !component || component.isDemo || component.isDisabled || component.tableResults === 0;

        if (notPermitted) {
            // @ts-ignore FIXME: strict migration - 'customUrlObj.query' is possibly 'undefined' — preserves pre-migration behavior; not_permitted assignment relied on query being initialized by an upstream builder
            customUrlObj.query.not_permitted = true;
        }
        return customUrlObj;
    } catch (e) {
        Sentry.fatal("Error in CustomUrlBuilderService", e as Error);
        try {
            return commonBuilder(pageId, params);
        } catch (e) {
            Sentry.fatal("Error in CustomUrlBuilderService", e as Error);
            // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'ICustomDimensionData' — preserves pre-migration behavior; caller handles null as a soft failure signal
            return null;
        }
    }
};

///////////////
// BUILDERS
///////////////
